import { API_URL } from "config";
import { useMutation } from "@tanstack/react-query";
import { managedFetch } from "utils/FetchManager";
import { useSnackbar } from "notistack";
import { useAuth } from "contexts/AuthContext";

export default function useQueryMutation(
  endpoint,
  method = "POST",
  options = {}
) {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const AUTH_TOKEN = currentUser?.accessToken;

  const { mutate, isPending, error, isSuccess } = useMutation({
    mutationFn: async (payload) => {
      if (!AUTH_TOKEN) {
        throw new Error("No authentication token available");
      }

      const response = await managedFetch(
        `${API_URL}${endpoint}`,
        {
          method: method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${AUTH_TOKEN}`,
          },
          body: JSON.stringify(payload),
        },
        currentUser
      );

      const data = await response.json();
      if (response.status === 400) {
        return {
          status: 400,
          message: "format-error",
          // Surface the server's explanation (FastAPI puts it in `detail`) so
          // callers can show why the request was rejected instead of silently
          // treating the 400 as a no-op success.
          detail: data?.detail,
        };
      }
      if (response.status >= 300) {
        throw new Error(`Failed to call ${method} ${endpoint}`);
      }
      return data;
    },
    onSuccess: options.onSuccess,
    onError: (e) => {
      console.error(`Error calling ${method} ${endpoint}: ${e}`);
      enqueueSnackbar("Error calling " + method + " " + endpoint, {
        variant: "error",
      });
      options.onError && options.onError(e);
    },
  });

  return { mutate, isPending, error, isSuccess };
}
